import { useSyncExternalStore } from 'react';
import { Workbox } from 'workbox-window';

/**
 * Service-worker updates. Pages are always loaded from the server while online (vite.config.ts),
 * so a fresh load already runs the latest deploy; this module covers pages that are open when a
 * deploy happens. The generated worker activates itself as soon as it is installed, then:
 *  - nothing typed on the page yet: reload at once;
 *  - something typed: show the "new version" notice and reload on the next in-app navigation
 *    (never in the middle of a form).
 * Updates are looked for every 10 minutes and whenever the tab becomes visible again. A lazy
 * chunk of the old version that the new deploy removed reloads the page instead of failing.
 */

const CHECK_EVERY_MS = 10 * 60_000;

let ready = false;
const listeners = new Set<() => void>();

function markReady() {
  if (ready) return;
  ready = true;
  listeners.forEach((l) => l());
}

/** True once a newer version has taken over the cache: the next full load shows it. */
export function updateReady(): boolean {
  return ready;
}

export function useUpdateReady(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => ready,
    () => false,
  );
}

export function applyUpdate(): void {
  window.location.reload();
}

/** A chunk of the version this page was loaded with is gone (new deploy): load the new version once. */
function reloadOnStaleChunks(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const key = 'kfms.staleChunkReload';
    try {
      const last = Number(sessionStorage.getItem(key) ?? 0);
      if (Date.now() - last < 60_000) return; // already tried: let the error surface
      sessionStorage.setItem(key, String(Date.now()));
    } catch {
      /* storage unavailable: reload anyway */
    }
    event.preventDefault();
    window.location.reload();
  });
}

export function registerServiceWorker(): void {
  reloadOnStaleChunks();
  if (!('serviceWorker' in navigator)) return;
  let typed = false;
  document.addEventListener('input', () => (typed = true), { capture: true, once: true });
  const wb = new Workbox('/sw.js', { scope: '/' });
  wb.addEventListener('activated', (event) => {
    if (!event.isUpdate && !event.isExternal) return; // first install: the page already runs this version
    if (!typed) applyUpdate();
    else markReady();
  });
  void wb.register({ immediate: true });
  const check = () => {
    if (navigator.onLine && !ready) void wb.update().catch(() => undefined);
  };
  window.setInterval(check, CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}
